import axios from 'axios';


const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add the auth token to headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const isLoginRequest = error.config.url?.includes('/token/');
      if (!isLoginRequest) {
        const token = localStorage.getItem('access_token');
        if (token) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (response.config.url?.includes('/parts/') && response.config.method === 'get' && response.config.params?.search) {
      window.dispatchEvent(new CustomEvent('sales-search', {
        detail: { query: response.config.params.search },
      }));
    }
    if (response.config.url?.includes('/parts/search/') && response.config.method === 'get') {
      window.dispatchEvent(new CustomEvent('sales-search', {
        detail: { query: response.config.params?.q || '' },
      }));
    }
    return response;
  },
  (error) => Promise.reject(error)
);

export const loginUser = async (username, password, captchaId = null, captchaCode = null) => {
  try {
    const payload = { username, password };
    if (!captchaId || !captchaCode) {
      throw new Error('کد امنیتی الزامی است');
    }
    payload.captcha_id = captchaId;
    payload.captcha_code = captchaCode;
    const response = await api.post('/auth/token/', payload);
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);
    return response.data;
  } catch (error) {
    console.error("Error logging in:", error);
    throw error;
  }
};

export const demoLogin = async () => {
  const response = await api.post('/demo-login/');
  localStorage.setItem('access_token', response.data.access);
  localStorage.setItem('refresh_token', response.data.refresh);
  return response.data;
};

export const fetchLoginCaptcha = async () => {
  try {
    const response = await api.get('/auth/captcha/login-code/');
    return response.data;
  } catch (error) {
    console.error("Error fetching captcha:", error);
    throw error;
  }
};

export const logoutUser = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

export const registerUser = async (username, password, company_name = '', plan_slug = 'startup', billing_type = 'monthly') => {
  try {
    const response = await api.post('/register/', { username, password, company_name, plan_slug, billing_type });
    return response.data;
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

// Users API
export const getUsers = async () => {
  try {
    const response = await api.get('/users/');
    return response.data;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const getUserById = async (id) => {
  try {
    const response = await api.get(`/users/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user with ID ${id}:`, error);
    throw error;
  }
};

export const updateUser = async (id, data) => {
  try {
    const response = await api.put(`/users/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating user with ID ${id}:`, error);
    throw error;
  }
};

export const deleteUser = async (id) => {
  try {
    const response = await api.delete(`/users/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting user with ID ${id}:`, error);
    throw error;
  }
};

// Parts API
export const getParts = async () => {
  try {
    const response = await api.get('/parts/');
    return response.data;
  } catch (error) {
    console.error("Error fetching parts:", error);
    throw error;
  }
};

export const getPartsPaginated = async (params = {}) => {
  try {
    const response = await api.get('/parts/', { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching paginated parts:", error);
    throw error;
  }
};

export const getPartById = async (id) => {
  try {
    const response = await api.get(`/parts/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching part with ID ${id}:`, error);
    throw error;
  }
};

export const searchParts = async (params = {}) => {
  try {
    const response = await api.get('/parts/search/', { params });
    return response.data;
  } catch (error) {
    console.error("Error searching parts:", error);
    throw error;
  }
};

export const createPart = async (data) => {
  try {
    const response = await api.post('/parts/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating part:", error);
    throw error;
  }
};

export const updatePart = async (id, data) => {
  try {
    const response = await api.put(`/parts/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating part with ID ${id}:`, error);
    throw error;
  }
};

export const deletePart = async (id) => {
  try {
    const response = await api.delete(`/parts/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting part with ID ${id}:`, error);
    throw error;
  }
};

// Inventory API
export const getInventory = async () => {
  try {
    const response = await api.get('/inventories/');
    return response.data;
  } catch (error) {
    console.error("Error fetching inventory:", error);
    throw error;
  }
};

export const getInventoryItemById = async (id) => {
  try {
    const response = await api.get(`/inventories/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching inventory item with ID ${id}:`, error);
    throw error;
  }
};

export const createInventoryItem = async (data) => {
  try {
    const response = await api.post('/inventories/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating inventory item:", error);
    throw error;
  }
};

export const updateInventoryItem = async (id, data) => {
  try {
    const response = await api.patch(`/inventories/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating inventory item with ID ${id}:`, error);
    throw error;
  }
};

export const getInventoryPaginated = async (params = {}) => {
  try {
    const response = await api.get('/inventories/', { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching paginated inventory:", error);
    throw error;
  }
};

export const deleteInventoryItem = async (id) => {
  try {
    const response = await api.delete(`/inventories/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting inventory item with ID ${id}:`, error);
    throw error;
  }
};


// Goods Receipt Notes API
export const getGoodsReceiptNotes = async () => {
  try {
    const response = await api.get('/goodsreceiptnotes/');
    return response.data;
  } catch (error) {
    console.error("Error fetching goods receipt notes:", error);
    throw error;
  }
};

export const getGoodsReceiptNoteById = async (id) => {
  try {
    const response = await api.get(`/goodsreceiptnotes/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching goods receipt note with ID ${id}:`, error);
    throw error;
  }
};

export const createGoodsReceiptNote = async (data) => {
  try {
    const response = await api.post('/goodsreceiptnotes/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating goods receipt note:", error);
    throw error;
  }
};

export const updateGoodsReceiptNote = async (id, data) => {
  try {
    const response = await api.put(`/goodsreceiptnotes/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating goods receipt note with ID ${id}:`, error);
    throw error;
  }
};

export const deleteGoodsReceiptNote = async (id) => {
  try {
    const response = await api.delete(`/goodsreceiptnotes/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting goods receipt note with ID ${id}:`, error);
    throw error;
  }
};

// Invoices API
export const getInvoices = async (params = {}) => {
  try {
    const response = await api.get('/invoices/', { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};

export const getInvoiceById = async (id) => {
  try {
    const response = await api.get(`/invoices/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching invoice with ID ${id}:`, error);
    throw error;
  }
};

export const createInvoice = async (data) => {
  try {
    const response = await api.post('/invoices/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating invoice:", error);
    throw error;
  }
};

export const updateInvoice = async (id, data) => {
  try {
    const response = await api.put(`/invoices/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating invoice with ID ${id}:`, error);
    throw error;
  }
};

export const deleteInvoice = async (id) => {
  try {
    const response = await api.delete(`/invoices/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting invoice with ID ${id}:`, error);
    throw error;
  }
};

// Sales Orders API
export const createSalesOrder = async (data) => {
  try {
    const response = await api.post('/salesorders/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating sales order:", error);
    throw error;
  }
};

export const getSalesOrders = async () => {
    try {
        const response = await api.get('/salesorders/');
        return response.data;
    } catch (error) {
        console.error("Error fetching sales orders:", error);
        throw error;
    }
};

export const getSalesOrderById = async (id) => {
    try {
        const response = await api.get(`/salesorders/${id}/`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching sales order with ID ${id}:`, error);
        throw error;
    }
};

export const updateSalesOrder = async (id, data) => {
    try {
        const response = await api.put(`/salesorders/${id}/`, data);
        return response.data;
    } catch (error) {
        console.error(`Error updating sales order with ID ${id}:`, error);
        throw error;
    }
};

export const deleteSalesOrder = async (id) => {
    try {
        const response = await api.delete(`/salesorders/${id}/`);
        return response.data;
    } catch (error) {
        console.error(`Error deleting sales order with ID ${id}:`, error);
        throw error;
    }
};

// Warehouses API
export const getWarehouses = async () => {
  try {
    const response = await api.get('/warehouses/');
    return response.data;
  } catch (error) {
    console.error("Error fetching warehouses:", error);
    throw error;
  }
};

export const getWarehouseById = async (id) => {
  try {
    const response = await api.get(`/warehouses/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching warehouse with ID ${id}:`, error);
    throw error;
  }
};

export const createWarehouse = async (data) => {
  try {
    const response = await api.post('/warehouses/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating warehouse:", error);
    throw error;
  }
};

export const updateWarehouse = async (id, data) => {
  try {
    const response = await api.put(`/warehouses/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating warehouse with ID ${id}:`, error);
    throw error;
  }
};

export const deleteWarehouse = async (id) => {
  try {
    const response = await api.delete(`/warehouses/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting warehouse with ID ${id}:`, error);
    throw error;
  }
};

// Locations API
export const getLocations = async (params = {}) => {
  try {
    const response = await api.get('/locations/', { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching locations:", error);
    throw error;
  }
};

export const getWarehouseLayoutTemplates = async () => {
  try {
    const response = await api.get('/warehouses/layout-templates/');
    return response.data;
  } catch (error) {
    console.error("Error fetching layout templates:", error);
    throw error;
  }
};

export const getWarehouseLayoutConfig = async (warehouseId) => {
  try {
    const response = await api.get(`/warehouses/${warehouseId}/layout/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching layout config for ${warehouseId}:`, error);
    throw error;
  }
};

export const updateWarehouseLayout = async (warehouseId, data) => {
  try {
    const response = await api.put(`/warehouses/${warehouseId}/layout/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating layout for ${warehouseId}:`, error);
    throw error;
  }
};

export const getLayoutPresets = async (warehouseId) => {
  try {
    const response = await api.get(`/warehouses/${warehouseId}/layout-presets/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching presets for ${warehouseId}:`, error);
    throw error;
  }
};

export const saveLayoutPreset = async (warehouseId, data) => {
  try {
    const response = await api.post(`/warehouses/${warehouseId}/layout-presets/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error saving preset for ${warehouseId}:`, error);
    throw error;
  }
};

export const updateLayoutPreset = async (warehouseId, presetId, data) => {
  try {
    const response = await api.put(`/warehouses/${warehouseId}/layout-presets/${presetId}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating preset ${presetId}:`, error);
    throw error;
  }
};

export const deleteLayoutPreset = async (warehouseId, presetId) => {
  try {
    const response = await api.delete(`/warehouses/${warehouseId}/layout-presets/${presetId}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting preset ${presetId}:`, error);
    throw error;
  }
};

export const getWarehouseNavigationMap = async (warehouseId) => {
  try {
    const response = await api.get(`/warehouses/${warehouseId}/navigation-map/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching navigation map for warehouse ${warehouseId}:`, error);
    throw error;
  }
};

export const getWarehousePath = async (warehouseId, params) => {
  try {
    const response = await api.get(`/warehouses/${warehouseId}/path/`, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching path for warehouse ${warehouseId}:`, error);
    throw error;
  }
};

export const getNearestNavigationPart = async (params) => {
  try {
    const response = await api.get('/navigation/nearest-part/', { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching nearest navigation part:", error);
    throw error;
  }
};

export const resolveNavigationQr = async (code) => {
  try {
    const response = await api.get('/navigation/resolve-qr/', { params: { code } });
    return response.data;
  } catch (error) {
    console.error(`Error resolving QR code ${code}:`, error);
    throw error;
  }
};

export const getLocationById = async (id) => {
  try {
    const response = await api.get(`/locations/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching location with ID ${id}:`, error);
    throw error;
  }
};

export const createLocation = async (data) => {
  try {
    const response = await api.post('/locations/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating location:", error);
    throw error;
  }
};

export const updateLocation = async (id, data) => {
  try {
    const response = await api.put(`/locations/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating location with ID ${id}:`, error);
    throw error;
  }
};

export const deleteLocation = async (id) => {
  try {
    const response = await api.delete(`/locations/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting location with ID ${id}:`, error);
    throw error;
  }
};

// Suppliers API
export const getSuppliers = async () => {
  try {
    const response = await api.get('/suppliers/');
    return response.data;
  } catch (error) {
    console.error("Error fetching suppliers:", error);
    throw error;
  }
};

export const getSupplierById = async (id) => {
  try {
    const response = await api.get(`/suppliers/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching supplier with ID ${id}:`, error);
    throw error;
  }
};

export const createSupplier = async (data) => {
  try {
    const response = await api.post('/suppliers/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating supplier:", error);
    throw error;
  }
};

export const updateSupplier = async (id, data) => {
  try {
    const response = await api.put(`/suppliers/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating supplier with ID ${id}:`, error);
    throw error;
  }
};

export const deleteSupplier = async (id) => {
  try {
    const response = await api.delete(`/suppliers/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting supplier with ID ${id}:`, error);
    throw error;
  }
};

// Categories API
export const getCategories = async () => {
  try {
    const response = await api.get('/categories/');
    return response.data;
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
};

export const getCategoryById = async (id) => {
  try {
    const response = await api.get(`/categories/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching category with ID ${id}:`, error);
    throw error;
  }
};

export const createCategory = async (data) => {
  try {
    const response = await api.post('/categories/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating category:", error);
    throw error;
  }
};

export const updateCategory = async (id, data) => {
  try {
    const response = await api.put(`/categories/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating category with ID ${id}:`, error);
    throw error;
  }
};

export const deleteCategory = async (id) => {
  try {
    const response = await api.delete(`/categories/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting category with ID ${id}:`, error);
    throw error;
  }
};

// Customers API (Assuming you have a customers endpoint, if not, this might need adjustment)
// For now, based on SalesOrderForm usage
export const getCustomers = async () => {
    try {
        // If you don't have a dedicated customers app, this might be users or a specific group of users
        // Adjust the endpoint as necessary. 
        // Based on typical Django setups:
        const response = await api.get('/customers/'); 
        return response.data;
    } catch (error) {
        console.error("Error fetching customers:", error);
        throw error;
    }
};

// Settings API
export const getSettings = async () => {
    try {
        const response = await api.get('/settings/');
        return response.data;
    } catch (error) {
        console.error("Error fetching settings:", error);
        throw error;
    }
};

export const getSettingByKey = async (key) => {
    try {
        const response = await api.get(`/settings/${key}/`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching setting ${key}:`, error);
        throw error;
    }
};

export const updateSettingByKey = async (key, value) => {
    try {
        const response = await api.put(`/settings/${key}/`, { value });
        return response.data;
    } catch (error) {
        console.error(`Error updating setting ${key}:`, error);
        throw error;
    }
};

export const fetchUsdRate = async () => {
    try {
        const response = await api.post('/fetch-usd-rate/');
        return response.data;
    } catch (error) {
        console.error("Error fetching USD rate:", error);
        throw error;
    }
};

export const getPartPriceReferences = async (partId) => {
    try {
        const response = await api.get('/price-references/', { params: { part: partId } });
        return response.data;
    } catch (error) {
        console.error("Error fetching price references:", error);
        throw error;
    }
};

export const getPartPriceSummary = async (partId) => {
    try {
        const response = await api.get(`/parts/${partId}/price-summary/`);
        return response.data;
    } catch (error) {
        console.error("Error fetching price summary:", error);
        throw error;
    }
};

export const getPartPriceBreakdown = async (partId) => {
    try {
        const response = await api.get(`/parts/${partId}/price-breakdown/`);
        return response.data;
    } catch (error) {
        console.error("Error fetching price breakdown:", error);
        throw error;
    }
};

// Plans & Feature Gating API
export const getMyCompanyPlan = async () => {
    try {
        const response = await api.get('/my-company-plan/');
        return response.data;
    } catch (error) {
        console.error("Error fetching company plan:", error);
        throw error;
    }
};

export const updateMyCompanyPlan = async (data) => {
    try {
        const response = await api.put('/my-company-plan/', data);
        return response.data;
    } catch (error) {
        console.error("Error updating company plan:", error);
        throw error;
    }
};

export const checkFeatureAccess = async (featureSlug) => {
    try {
        const response = await api.get(`/check-feature-access/${featureSlug}/`);
        return response.data;
    } catch (error) {
        console.error(`Error checking feature ${featureSlug}:`, error);
        throw error;
    }
};

export const getPlans = async (params = {}) => {
    try {
        const response = await api.get('/plans/', { params });
        return response.data;
    } catch (error) {
        console.error("Error fetching plans:", error);
        throw error;
    }
};

export const getFeatures = async (params = {}) => {
    try {
        const response = await api.get('/features/', { params });
        return response.data;
    } catch (error) {
        console.error("Error fetching features:", error);
        throw error;
    }
};

export const getPlanFeatures = async (params = {}) => {
    try {
        const response = await api.get('/plan-features/', { params });
        return response.data;
    } catch (error) {
        console.error("Error fetching plan features:", error);
        throw error;
    }
};

export const getPendingApprovals = async () => {
    try {
        const response = await api.get('/users/pending-approvals/');
        return response.data;
    } catch (error) {
        console.error("Error fetching pending approvals:", error);
        throw error;
    }
};

export const approveUser = async (userId) => {
    try {
        const response = await api.post(`/users/${userId}/approve/`);
        return response.data;
    } catch (error) {
        console.error("Error approving user:", error);
        throw error;
    }
};

export const getCompanies = async (params = {}) => {
    try {
        const response = await api.get('/companies/', { params });
        return response.data;
    } catch (error) {
        console.error("Error fetching companies:", error);
        throw error;
    }
};

export const topUpWallet = async (amount, note = '') => {
    try {
        const response = await api.post('/my-company-plan/top-up/', { amount, note });
        return response.data;
    } catch (error) {
        console.error("Error topping up wallet:", error);
        throw error;
    }
};

export const searchPartImages = async (query) => {
  try {
    const response = await api.get('/parts/search-images/', { params: { q: query } });
    return response.data;
  } catch (error) {
    console.error("Error searching images:", error);
    throw error;
  }
};

export const downloadAndSaveImage = async (url, partId) => {
  try {
    const response = await api.post('/parts/download-image/', { url, part_id: partId });
    return response.data;
  } catch (error) {
    console.error("Error downloading image:", error);
    throw error;
  }
};

export const getPartImages = async (partId) => {
  try {
    const response = await api.get('/part-images/', { params: { part: partId } });
    return response.data;
  } catch (error) {
    console.error("Error fetching part images:", error);
    throw error;
  }
};

export const createPartImage = async (formData) => {
  try {
    const response = await api.post('/part-images/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error("Error creating part image:", error);
    throw error;
  }
};

export const deletePartImage = async (id) => {
  try {
    const response = await api.delete(`/part-images/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting part image ${id}:`, error);
    throw error;
  }
};

export const setPrimaryImage = async (id) => {
  try {
    const response = await api.patch(`/part-images/${id}/`, { image_type: 'main' });
    return response.data;
  } catch (error) {
    console.error("Error setting primary image:", error);
    throw error;
  }
};

export const changePlan = async (planId, billingType = null) => {
    try {
        const response = await api.post('/my-company-plan/change-plan/', { plan_id: planId, billing_type: billingType });
        return response.data;
    } catch (error) {
        console.error("Error changing plan:", error);
        throw error;
    }
};

// ─── Online Store API (public) ───────────────────────────────────────────

export const getStoreProducts = async (params = {}) => {
  try {
    const response = await api.get('/store/products/', { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching store products:", error);
    throw error;
  }
};

export const getStoreCategories = async (params = {}) => {
  try {
    const response = await api.get('/store/categories/', { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching store categories:", error);
    throw error;
  }
};

export const createStoreOrder = async (data) => {
  try {
    const response = await api.post('/store/orders/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating store order:", error);
    throw error;
  }
};

export const getStoreInfo = async (companySlug) => {
  try {
    const response = await api.get('/store/info/', { params: { company_slug: companySlug } });
    return response.data;
  } catch (error) {
    console.error("Error fetching store info:", error);
    throw error;
  }
};

export const uploadPaymentProof = async (formData) => {
  try {
    const response = await api.post('/store/orders/upload-proof/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading payment proof:", error);
    throw error;
  }
};

export const confirmPayment = async (orderId) => {
  try {
    const response = await api.post(`/salesorders/${orderId}/confirm_payment/`);
    return response.data;
  } catch (error) {
    console.error("Error confirming payment:", error);
    throw error;
  }
};

export const updateOrderStatus = async (orderId, status) => {
  try {
    const response = await api.post(`/salesorders/${orderId}/update_status/`, { status });
    return response.data;
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
};

export const simulatePayment = async (orderId) => {
  try {
    const response = await api.post('/store/orders/pay/', { order_id: orderId });
    return response.data;
  } catch (error) {
    console.error("Error processing payment:", error);
    throw error;
  }
};

export const trackOrderByPhone = async (phone, companySlug) => {
  try {
    const params = { phone };
    if (companySlug) params.company_slug = companySlug;
    const response = await api.get('/store/orders/track/', { params });
    return response.data;
  } catch (error) {
    console.error("Error tracking order:", error);
    throw error;
  }
};

// Speaker Profiles API
export const getSpeakerProfiles = async () => {
  try {
    const response = await api.get('/chatbot/speaker-profiles/');
    return response.data;
  } catch (error) {
    console.error("Error fetching speaker profiles:", error);
    throw error;
  }
};

export const getSpeakerProfileById = async (id) => {
  try {
    const response = await api.get(`/chatbot/speaker-profiles/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching speaker profile ${id}:`, error);
    throw error;
  }
};

export const createSpeakerProfile = async (data) => {
  try {
    const response = await api.post('/chatbot/speaker-profiles/', data);
    return response.data;
  } catch (error) {
    console.error("Error creating speaker profile:", error);
    throw error;
  }
};

export const updateSpeakerProfile = async (id, data) => {
  try {
    const response = await api.put(`/chatbot/speaker-profiles/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating speaker profile ${id}:`, error);
    throw error;
  }
};

export const deleteSpeakerProfile = async (id) => {
  try {
    const response = await api.delete(`/chatbot/speaker-profiles/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting speaker profile ${id}:`, error);
    throw error;
  }
};

export const enrollSellerVoice = async (formData) => {
  try {
    const response = await api.post('/chatbot/enroll-seller-voice/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error("Error enrolling seller voice:", error);
    throw error;
  }
};
